import { Box, DialogActions, Typography } from "@mui/material";
import {
	ContractPeriodType,
	Operator,
} from "../../../../../requests_cm/gecoContractsService/types";
import {
	borderNeutralSecondary,
	textNeutralSecondary,
	white,
} from "../../../../../core/theme";
import { noop } from "../../../../../common/utils/operations";
import { Formik } from "formik";
import { CurtailabilityIndexInput } from "./CurtailabilityIndexInputs";
import { PriceIndex } from "../../../../../requests_cm/gecoReferentialService/types";
import { CurtailabilityComparator } from "./CurtailabilityComparator";
import { NJFormItem } from "@engie-group/fluid-design-system-react";
import AlertDialogModal from "../../../../../common/components/AlertDialogModal";
import { useCallback, useEffect, useState } from "react";
import { Toggle } from "../../../../../common/components/Toggle";
import { If } from "../../../../../common/components/If";
import { PrimaryButton } from "../../../../../common/components/CustomButton";
import { FluidButton } from "../../../../../common/components/FluidButton";
import { isStringNullOrEmpty } from "../../../../../common/utils/isStringNullorEmpty";

export interface CurtailabilityPriceBoxProps {
	contractPeriod?: ContractPeriodType;
	priceIndexes: PriceIndex[];
	toggleCurtailabilityForward: (isToggled?: boolean) => void;
	onRemoveCurtailability: () => void;
	onSubmit: (formikData: any) => void;
	hasCurtailability: boolean;
}

const style = {
	container: {
		width: "680px",
		padding: "16px",
		display: "flex",
		flexDirection: "column",
		border: `1px solid ${borderNeutralSecondary}`,
		gap: "16px",
		background: white,
	},
	title: {
		color: textNeutralSecondary,
		fontSize: "16px",
		fontWeight: "600",
	},
	toogleBox: {
		width: "100%",
		display: "flex",
		justifyContent: "space-between",
	},
	fields: {
		display: "flex",
		flexDirection: "row",
		gap: "8px",
	},
};

export function CurtailabilityPriceBox(
	props: Readonly<CurtailabilityPriceBoxProps>
) {
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
		useState(false);

	const onToggleCurtailabilityForward = useCallback(
		(isToggled: boolean) => {
			if (!isToggled && props.contractPeriod?.curtailability?.index) {
				setIsConfirmationModalOpen(true);
			} else {
				props.toggleCurtailabilityForward(isToggled);
			}
		},
		[props.toggleCurtailabilityForward, props.contractPeriod]
	);

	const confirmRemoveCurtailabilityForwardCondition = useCallback(() => {
		props.toggleCurtailabilityForward(false);
		setIsConfirmationModalOpen(false);
		props.onRemoveCurtailability();
	}, [
		props.onRemoveCurtailability,
		setIsConfirmationModalOpen,
		props.onSubmit,
		props.onRemoveCurtailability,
	]);

	useEffect(() => {
		if (props.contractPeriod) {
			onToggleCurtailabilityForward(
				!!props.contractPeriod?.curtailability
			);
		}
	}, [props.contractPeriod]);

	return (
		<>
			<Box sx={style.container}>
				<Formik
					validateOnChange={false}
					validateOnBlur={false}
					enableReinitialize
					initialValues={{
						index:
							props.contractPeriod?.curtailability?.index ?? -1,
						threshold:
							props.contractPeriod?.curtailability?.threshold ??
							0,
						comparator:
							props.contractPeriod?.curtailability?.comparator ??
							Operator.LOWER_THAN,
						nb_consecutive_blocks:
							props.contractPeriod?.curtailability
								?.nb_consecutive_blocks ?? 0,
					}}
					onSubmit={props.onSubmit}
				>
					{({
						values,
						handleChange,
						resetForm,
						handleSubmit,
						dirty,
					}) => (
						<form onSubmit={handleSubmit}>
							<Box sx={style.toogleBox}>
								<Typography style={style.title}>
									Curtailability Forward
								</Typography>
								<Box
									onClick={() =>
										onToggleCurtailabilityForward(
											!props.hasCurtailability
										)
									}
								>
									<Toggle
										onChange={noop}
										isTrue={props.hasCurtailability}
										name={""}
										label={"Enable Curtailability Forward"}
										description=""
									/>
								</Box>
							</Box>
							<If condition={props.hasCurtailability}>
								<Box sx={style.fields}>
									<CurtailabilityIndexInput
										currentCurtailabilityPriceIndex={
											values?.index
										}
										onChange={handleChange}
										priceIndexes={props.priceIndexes}
									/>
									<CurtailabilityComparator
										onChange={handleChange}
										comparator={values?.comparator}
									/>
									<NJFormItem
										id="threshold"
										label="Threshold"
										labelKind="static"
										name="threshold"
										type="number"
										// @ts-ignore Typing issue coming from package, expecting string...
										value={values.threshold}
										onChange={handleChange}
										title="threshold"
										wrap="soft"
									/>
									<NJFormItem
										id="nb_consecutive_blocks"
										label="Consecutive blocks"
										labelKind="static"
										name="nb_consecutive_blocks"
										type="number"
										// @ts-ignore Typing issue coming from package, expecting string...
										value={values.nb_consecutive_blocks}
										onChange={handleChange}
										title="Consectuive blocks"
										wrap="soft"
									/>
								</Box>
							</If>
							<DialogActions>
								<If condition={dirty}>
									<PrimaryButton
										onClick={() => resetForm()}
										text="Reset"
										type="button"
										color="secondary"
									/>
									<FluidButton
										type="submit"
										label={"Save"}
										isDisabled={
											!values.index ||
											!values.comparator ||
											!values.nb_consecutive_blocks ||
											isStringNullOrEmpty(String(values.threshold))
										}
										onClick={noop}
									/>
								</If>
							</DialogActions>
						</form>
					)}
				</Formik>
			</Box>
			<AlertDialogModal
				title={"Confirm Curtailability Condition Removal"}
				content="By approving you will remove all negative conditions on all settlement objects as well."
				isOpen={isConfirmationModalOpen}
				handleClose={() => setIsConfirmationModalOpen(false)}
				handleApprove={confirmRemoveCurtailabilityForwardCondition}
			/>
		</>
	);
}
